/**
 * Generic touch capability detection (any touch support present, including hybrids).
 */
export function hasAnyTouch() {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;
  return "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
}

/**
 * Internal: gather pointer media query facts safely.
 */
function getPointerProfile() {
  let primaryCoarse = false;
  let primaryFine = false;
  let anyCoarse = false;
  let anyFine = false;
  if (typeof window !== "undefined" && window.matchMedia) {
    try {
      primaryCoarse = window.matchMedia("(pointer: coarse)").matches;
      primaryFine = window.matchMedia("(pointer: fine)").matches;
      anyCoarse = window.matchMedia("(any-pointer: coarse)").matches;
      anyFine = window.matchMedia("(any-pointer: fine)").matches;
    } catch (_) {
      // ignore
    }
  }
  return { primaryCoarse, primaryFine, anyCoarse, anyFine };
}

/**
 * Touch-primary devices (phones/tablets):
 *  - Have touch capability AND primary pointer is coarse AND no primary fine pointer.
 *  - If primary queries are inconclusive, fall back to: any coarse pointer AND no fine pointer.
 */
export function isTouchPrimaryDevice() {
  if (!hasAnyTouch()) return false;
  const { primaryCoarse, primaryFine, anyCoarse, anyFine } =
    getPointerProfile();
  if (primaryCoarse && !primaryFine) return true;
  if (!primaryCoarse && !primaryFine) {
    // unknown primary, rely on any-* set
    if (anyCoarse && !anyFine) return true;
  }
  return false;
}

/**
 * Hybrid devices: have both a coarse (touch) and fine (mouse/trackpad) pointer.
 */
export function isHybridTouchDevice() {
  if (!hasAnyTouch()) return false;
  const { anyCoarse, anyFine } = getPointerProfile();
  return anyCoarse && anyFine;
}

// Viewport helpers aligned with SCSS breakpoints (xs+sm mobile, md tablet, lg+xl desktop)
import { breakpoint, inRange, BREAKPOINTS } from "./breakpoints";

export function isMobileViewport() {
  // xs + sm ranges (0 - SM_MAX)
  return breakpoint.isSmDown();
}

export function isTabletViewport() {
  // md range (MD_MIN - MD_MAX)
  return breakpoint.isMd();
}

export function isDesktopViewport() {
  // lg + xl (LG_MIN+)
  return breakpoint.isLgUp();
}

/**
 * Large desktop viewport (within the bounded large range only).
 */
export function isLargeDesktopViewport() {
  return inRange(BREAKPOINTS.LG_MIN, BREAKPOINTS.LG_MAX);
}

/**
 * Extra-large viewport (above LG_MAX / at XL_MIN and beyond).
 */
export function isXlViewport() {
  return breakpoint.isXl();
}

/**
 * Backward compatible: treat "mobile" as touch-primary handheld (exclude hybrids).
 */
export function isMobileTouchScreen() {
  return isTouchPrimaryDevice();
}

export function isMobile() {
  return isTouchPrimaryDevice();
}

// (Optional) default export matching the original named export usage.
export default isMobile;
